/**
 * useAffaireDetail — Master hook for the Affaire Detail Page
 * Composes existing hooks from tracker and fetches additional data
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchAPI } from '@/features/admin/tabs/search/api/search.api';
import { trackerAPI } from '@/features/admin/tabs/tracker/api/tracker.api';
import { affaireDetailAPI } from '../api/affaire-detail.api';
import { apiClient } from '@/core/api/client';
import type { Affaire } from '@/features/admin/tabs/search/types/search.types';
import type { AffaireContact } from '@/features/admin/tabs/tracker/types/tracker.types';
import type { AffaireDetailTab, BienData } from '../types/affaire-detail.types';

export function useAffaireDetail(affaireId: number | null) {
  const [activeTab, setActiveTab] = useState<AffaireDetailTab>('resume');

  // Core affaire data
  const {
    data: affaireData,
    isLoading: affaireLoading,
    refetch: refetchAffaire
  } = useQuery({
    queryKey: ['affaire-detail', affaireId],
    queryFn: async () => {
      if (!affaireId) return null;

      // For new dossiers (>= 90000), try the dossiers API first
      if (affaireId >= 90000) {
        try {
          const dossierRes: any = await apiClient.get(`/api/dossiers/${affaireId}`);
          if (dossierRes.success && dossierRes.data) {
            const d = dossierRes.data;
            // Map dossier fields to Affaire shape for compatibility
            return {
              id: String(d.id),
              id_unique: String(d.id),
              k_id_dossier: d.id_dossier_expert || '',
              date_demande: d.date_demande,
              date_sortie: d.date_sortie,
              date_du_rv: d.date_rv,
              heure_rv: d.heure_rv ? String(d.heure_rv) : undefined,
              type_mission: [d.type, d.sub_type].filter(Boolean).join(' '),
              statut_affaire: d.status,
              montant_tvac_affaire: d.montant_tvac ? Number(d.montant_tvac) : undefined,
              address: {
                number: d.property_number,
                street: d.property_street,
                postcode: d.property_postcode,
                locality: d.property_city,
              },
              expert: null,
              responsable_name: null,
              facturation: {},
              // New dossier fields
              honoraires_expert: d.honoraires_expert,
              code_produit: d.code_produit,
              ref_client: d.ref_client,
              projet: d.projet,
              deadline: d.deadline,
              date_in: d.date_in,
              date_eval1: d.date_eval1,
              date_eval2: d.date_eval2,
              date_eval3: d.date_eval3,
              date_out: d.date_out,
              date_reception_docs: d.date_reception_docs,
              date_paiement: d.date_paiement,
              ref_banque: d.ref_banque,
            } as Affaire;
          }
        } catch { /* fall through to FM search */ }
      }

      const res = await searchAPI.searchAffaire(String(affaireId));
      if (res.success && res.data?.affaire) return res.data.affaire;
      if (res.success && res.data?.affaires?.[0]) return res.data.affaires[0];
      return null;
    },
    enabled: !!affaireId,
    staleTime: 2 * 60 * 1000,
  });

  // Contacts
  const {
    data: contactsData,
    isLoading: contactsLoading,
    refetch: refetchContacts,
  } = useQuery({
    queryKey: ['affaire-detail-contacts', affaireId],
    queryFn: async () => {
      if (!affaireId) return [];

      // For new dossiers, get contacts from dossier_contacts
      if (affaireId >= 90000) {
        try {
          const dRes: any = await apiClient.get(`/api/dossiers/${affaireId}`);
          if (dRes.success && dRes.data?.contacts) {
            return dRes.data.contacts.map((c: any) => ({
              k_id_contact: c.customer_id,
              contact_row_id: c.id,  // dossier_contacts.id for removal
              nom_representant: c.last_name,
              prenom: c.first_name,
              email_1: c.email,
              tel_mobile: c.phone || c.phone_mobile,
              roles: [c.role.toUpperCase()],
            }));
          }
        } catch { /* fall through */ }
      }

      const res = await trackerAPI.getAffaireContacts(affaireId);
      return res.success ? res.data.contacts : [];
    },
    enabled: !!affaireId,
    staleTime: 5 * 60 * 1000,
  });

  // Biens (property data)
  const {
    data: biensData,
    isLoading: biensLoading
  } = useQuery({
    queryKey: ['affaire-detail-biens', affaireId],
    queryFn: async () => {
      if (!affaireId) return [];
      const res = await affaireDetailAPI.getAffaireBiens(String(affaireId));
      return res.success ? (res.data?.biens || []) : [];
    },
    enabled: !!affaireId,
    staleTime: 5 * 60 * 1000,
  });

  // Payment summary
  const {
    data: paymentData,
    isLoading: paymentLoading
  } = useQuery({
    queryKey: ['affaire-detail-payment', affaireId],
    queryFn: async () => {
      if (!affaireId) return null;
      try {
        const res = await trackerAPI.getJustifyingItems(affaireId);
        return res.success ? res : null;
      } catch { return null; }
    },
    enabled: !!affaireId,
    staleTime: 5 * 60 * 1000,
  });

  // Fiche bien completion (lazy - only when tab active)
  const {
    data: ficheCompletion,
    isLoading: ficheLoading
  } = useQuery({
    queryKey: ['affaire-detail-fiche', affaireId],
    queryFn: async () => {
      if (!affaireId) return null;
      try {
        const res = await affaireDetailAPI.getFicheBienCompletion(String(affaireId));
        return res.success ? res.data : null;
      } catch { return null; }
    },
    enabled: !!affaireId && activeTab === 'fiche-bien',
    staleTime: 5 * 60 * 1000,
  });

  return {
    // Data
    affaire: affaireData as Affaire | null,
    contacts: (contactsData || []) as AffaireContact[],
    biens: (biensData || []) as BienData[],
    paymentData,
    ficheCompletion,

    // Loading states
    loading: affaireLoading,
    contactsLoading,
    biensLoading,
    paymentLoading,
    ficheLoading,

    // Tab state
    activeTab,
    setActiveTab,

    // Actions
    refetchAffaire,
    refetchContacts,
  };
}
